import React, { useEffect, useState, useRef, type FC } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useStudies } from '@/hooks/useStudies';
import {
  useDashboardStudySites,
  type DashboardStudySite,
} from '@/hooks/useDashboardStudySites';
import { SiteCard } from './SiteCard';

interface ActiveSitesContentProps {
  isLoading: boolean;
  sites: DashboardStudySite[];
}

const ActiveSitesContent: FC<ActiveSitesContentProps> = ({
  isLoading,
  sites,
}) => {
  if (isLoading) {
    return (
      <div className="flex gap-4">
        {[1, 2, 3].map(i => (
          <Skeleton key={i} className="h-[200px] w-[300px] flex-shrink-0" />
        ))}
      </div>
    );
  }

  return (
    <>
      {sites.map(site => (
        <SiteCard key={site.id} site={site} />
      ))}
    </>
  );
};

export const ActiveSitesStatus: React.FC = () => {
  const { t } = useTranslation();
  const { studies, isLoading: isLoadingStudies } = useStudies();
  const [selectedStudyId, setSelectedStudyId] = useState('');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    if (!isLoadingStudies && studies.length > 0 && !selectedStudyId) {
      const [defaultStudy] = studies;
      setSelectedStudyId(defaultStudy.id.toString());
    }
  }, [isLoadingStudies, studies, selectedStudyId]);

  const { sites, isLoading: isLoadingSites } =
    useDashboardStudySites(selectedStudyId);

  const isLoading =
    isLoadingStudies ||
    isLoadingSites ||
    (studies.length > 0 && !selectedStudyId);

  const updateScrollState = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const timeoutId = setTimeout(updateScrollState, 0);
    el.addEventListener('scroll', updateScrollState);
    window.addEventListener('resize', updateScrollState);
    return () => {
      clearTimeout(timeoutId);
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [sites, isLoading]);

  const scrollRight = () =>
    scrollContainerRef.current?.scrollBy({ left: 300, behavior: 'smooth' });
  const scrollLeft = () =>
    scrollContainerRef.current?.scrollBy({ left: -300, behavior: 'smooth' });

  if (!isLoading && studies.length === 0) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-[10px] p-6 mb-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-normal text-[#101828]">
            {t('dashboard.active_site_status')}
          </h2>
          {studies.length > 0 && (
            <Select
              disabled={isLoadingStudies}
              value={selectedStudyId}
              onValueChange={value => setSelectedStudyId(value)}
            >
              <SelectTrigger className="w-[160px] bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-[#101828] hover:border-gray-400 focus:border-gray-400 focus:ring-2 focus:ring-gray-200">
                <SelectValue
                  placeholder={
                    isLoadingStudies ? t('dashboard.loading_studies') : t('dashboard.select_study')
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {studies.map(study => (
                  <SelectItem key={study.id} value={study.id.toString()}>
                    {study.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        {selectedStudyId && (
          <Link
            to={`/studies/${selectedStudyId}`}
            className="flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
          >
            <span>{t('dashboard.view_study')}</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        )}
      </div>

      <div className="relative">
        <div
          ref={scrollContainerRef}
          className="overflow-x-auto pb-2 -mx-6 px-6 scrollbar-hide"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          <div
            ref={contentRef}
            className="flex gap-4"
            style={{ width: 'max-content' }}
          >
            <ActiveSitesContent isLoading={isLoading} sites={sites} />
          </div>
        </div>
        {canScrollLeft && !isLoading && sites.length > 0 && (
          <button
            onClick={scrollLeft}
            className="absolute left-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white border border-gray-200 rounded-full p-2 shadow-md hover:shadow-lg transition-shadow z-10"
            aria-label="Scroll left"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
        )}
        {canScrollRight && !isLoading && sites.length > 0 && (
          <button
            onClick={scrollRight}
            className="absolute right-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white border border-gray-200 rounded-full p-2 shadow-md hover:shadow-lg transition-shadow z-10"
            aria-label="Scroll right"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        )}
      </div>
    </div>
  );
};
